import React from 'react';
import { ScrollText, Shield, FileCheck, AlertCircle } from 'lucide-react';
import { Row, Col, Input, SectionTitle, FormField, Checkbox, FileInput, SignatureInput } from '../../../SharedInputs.jsx';
import { prettyChecklist, getCurrentDate } from '../utils.js';
import { S } from '../../styles.js';

export function SectionDeclaration({ value, onChange, errors = [], dispatch }) {
  const updateField = (field, fieldValue) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const toggleChecklist = (key) => {
    dispatch({ type: 'TOGGLE_CHECKLIST', key });
  };

  const getFieldError = (fieldName) => {
    return errors.find(error => error.toLowerCase().includes(fieldName.toLowerCase()));
  };

  // Auto-set today's date if not already set
  React.useEffect(() => {
    if (!value.signatureDate) {
      updateField('signatureDate', getCurrentDate());
    }
  }, []);

  return (
    <div>
      <SectionTitle icon={ScrollText} step={13}>
        Declaration, Privacy & Checklist
      </SectionTitle>

      {/* Privacy Notice */}
      <div style={{
        padding: 20,
        background: '#f8faff',
        borderRadius: 12,
        border: '1px solid #e0e7ff',
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Shield size={24} color="#667eea" style={{ marginTop: 4, flexShrink: 0 }} />
          <div>
            <h4 style={{ margin: '0 0 8px', color: '#374151' }}>Privacy Notice</h4>
            <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5, margin: 0 }}>
              CIET collects personal information to process your enrollment application, 
              provide educational services, and meet regulatory requirements. Your information 
              may be shared with government agencies, assessment bodies, and other education 
              providers as required. You have the right to access and correct your personal 
              information. For full details, please refer to our <a href='https://drive.google.com/file/d/12AqBqf_n0RsX1e-B0YhMRBxKjZadAskO/view?usp=sharing'>Privacy Policy.</a>
            </p>
          </div>
        </div>
      </div>

      {/* Declaration Checkboxes */}
      <Row>
        <Col span={12}>
          <FormField error={getFieldError('confirm')}>
            <Checkbox
              checked={value.confirmTrueAndCorrect}
              onChange={(e) => updateField('confirmTrueAndCorrect', e.target.checked)}
              error={!!getFieldError('confirm')}
            >
              <strong>I declare that the information provided in this application is true and correct.</strong>
            </Checkbox>
          </FormField>
          
          <FormField error={getFieldError('privacy')}>
            <Checkbox
              checked={value.agreePrivacyNotice}
              onChange={(e) => updateField('agreePrivacyNotice', e.target.checked)}
              error={!!getFieldError('privacy')}
            >
              <strong>I agree to the collection, use and disclosure of my personal information as outlined in the <a href='https://drive.google.com/file/d/12AqBqf_n0RsX1e-B0YhMRBxKjZadAskO/view?usp=sharing'>Privacy Policy</a>.</strong>
            </Checkbox>
          </FormField>
        </Col>
      </Row>

      {/* Signature Fields */}
      <Row>
        <Col span={6}>
          <FormField 
            label="Full Name" 
            required
            error={getFieldError('signature name')}
            hint="Type your full name as it appears on your identification"
          >
            <Input
              required
              value={value.signatureName}
              onChange={(e) => updateField('signatureName', e.target.value)}
              placeholder="Enter your full name"
              error={!!getFieldError('signature name')}
            />
          </FormField>
        </Col>
        <Col span={6}>
          <FormField 
            label="Date" 
            required
            error={getFieldError('signature date')}
          >
            <Input
              type="date"
              required
              value={value.signatureDate}
              onChange={(e) => updateField('signatureDate', e.target.value)}
              error={!!getFieldError('signature date')}
            />
          </FormField>
        </Col>
      </Row>
      <Row>
        <Col span={5}>
          <FormField 
              label="Student Signature" 
              required 
              error={errors.signature}
          >
            <SignatureInput
              value={value.signature}
              onChange={(val) => updateField('signature', val || "")}
              error={getFieldError('signature')}
            />
          </FormField>
        </Col>
      </Row>

      {/* Document Checklist */}
      <div style={{ marginTop: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <FileCheck size={24} color="#374151" />
          <h3 style={{ margin: 0, fontSize: 18, color: '#374151' }}>
            Document Checklist
          </h3>
        </div>
        
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
          Please confirm which documents you have prepared for submission. 
          While not all documents may be applicable to your situation, 
          please check all that you have ready to submit.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 12,
            padding: 16,
            background: '#f9fafb',
            borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}
        >
          {Object.entries(value.checklist).map(([key, checked]) => (
            <div
              key={key}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr', // ✅ checkbox + content
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px 12px',
                background: '#fff',
                borderRadius: 6,
                border: '1px solid #e5e7eb'
              }}
            >
              {/* Keep your custom Checkbox */}
              <Checkbox
                checked={checked}
                onChange={() => toggleChecklist(key)}
                required
              />

              {/* Text + link in second column */}
              <div>
                <span style={{ fontSize: 14, color: '#374151' }}>
                  {prettyChecklist(key)}
                </span>
                {key === "gsFormAttached" && (
                  <div>
                    <a
                      href="https://drive.google.com/file/d/1qwGOVI6D1RSQO9jpdMEL3oV1ieLXTpjp/view"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#2563eb',
                        textDecoration: 'underline',
                        whiteSpace: 'nowrap',
                        fontSize: 14
                      }}
                    >
                      Download Form
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {getFieldError("Checklist") && (
          <label style={S.error}>
            <AlertCircle size={16} /> {getFieldError("Checklist")}
          </label>
        )}

        <div style={{
          marginTop: 16,
          padding: 12,
          background: '#fef7f7',
          borderRadius: 8,
          border: '1px solid #fecaca'
        }}>
           <p style={{ fontSize: 13, color: '#b91c1c', margin: 0, lineHeight: 1.6 }}>
              <strong style={{ fontSize: 14 }}>Important:</strong>
            </p>
            <p style={{ fontSize: 13, color: '#b91c1c', margin: '8px 0 0 0', lineHeight: 1.6 }}>
              Please ensure all documents are certified copies or originals where required. Incomplete applications may delay processing. If you're missing required documents, please contact our admissions team before submitting your application.
            </p>
            <p style={{ fontSize: 13, color: '#b91c1c', margin: '8px 0 0 0', lineHeight: 1.6 }}>
              Additionally, all documents must be provided as <strong>clear, high-quality scans</strong> to ensure readability and acceptance.
            </p>
        </div>
      

      {/* Final Instructions */}
      <div style={{
        marginTop: 24,
        padding: 20,
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: 12,
        border: '1px solid #bae6fd'
      }}>
        <h4 style={{ margin: '0 0 12px', color: '#0c4a6e' }}>Ready to Submit?</h4>
        <ul style={{ margin: 0, paddingLeft: 20, color: '#0369a1', fontSize: 14 }}>
          <li style={{ marginBottom: 8 }}>Review all sections for accuracy</li>
          <li style={{ marginBottom: 8 }}>Ensure required fields are completed</li>
          <li style={{ marginBottom: 8 }}>Prepare supporting documents for upload or email submission</li>
          <li>Click "Submit Application" to complete your enrollment</li>
        </ul>
      </div>

      {/* Additional Document Upload */}
      <div style={{ marginTop: 32 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#374151' }}>
          Additional Supporting Documents
        </h3>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
          Upload any additional supporting documents, such as identification, recommendation letters, or other relevant files.
        </p>

        <FormField>
          <FileInput
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(files) => {
              dispatch({
                type: 'SET_FILES',
                section: 'declaration', // use 'declaration' to separate from transcripts
                field: 'additionalDocuments',
                files: Array.from(files),
              });
            }}
            value={value.additionalDocuments}
            placeholder="Upload additional documents (PDF, images, or Word documents)"
            maxSize={15 * 1024 * 1024} // 15MB
          />
        </FormField>

        <div style={{
          marginTop: 16,
          padding: 12,
          background: '#fffbeb',
          borderRadius: 8,
          border: '1px solid #fed7aa'
        }}>
          <p style={{ fontSize: 13, color: '#b45309', margin: 0, lineHeight: 1.4 }}>
            <strong>Document Requirements:</strong> All uploaded documents should be clear, legible, and in PDF, image, or Word format. For non-English documents, please provide certified translations where applicable.
          </p>
        </div>
      </div>
    </div>
    </div>
  );
}